import { TabBarV2, LoaderCircleV2, UI_BODY_SM, colors2, icons, TooltipV2 } from "oolib";
import React from "react";
import { useSearchParamsState } from "@/utils/customHooks";
import { LAYOUT_CONSTANTS } from "../LAYOUT_CONSTANTS";
import { DASHBOARD_TAB_PARAM_KEY } from "./constants";
import { useGetDashboardMetrics } from "./hooks/useGetDashboardMetrics";
import { SM_PageHeader } from "../comps/SM_PageHeader";
import { Freshness } from "./comps/Freshness";
import { BarChart } from "../../components/dataviz/BarChart";
import { DashboardStatsBar } from "./comps/DashboardStatsBar";
import { RecentActivity } from "./RecentActivity";
import { AIAnnotationWidget } from "./AIAnnotationWidget";
// import { TagHealth } from "./TagHealth";
import { SentimentTopicsWidget } from "./SentimentTopics";
import { InsightsList } from "./InsightsList";
import { EvidenceFindingsView } from "./EvidenceFindingsView";
import { FindingsTab } from "./FindingsTab";
import { InsightsTab } from "./InsightsTab";
import { NewTab } from "./NewTab";
import { useGetQueryData } from "../../utils/react-query-hooks/general";
import { getAnnoMode } from "@/utils/getAnnoMode";
import { isFeatureDisabledOnProd } from "@/utils/isFeatureDisabledOnProd";
import { getTenant } from "@/TENANT_CONFIGS";

import { ActivePanelProvider } from "./comps/ActivePanelContext";
import { TagHealth } from "./TagHealth";
import { DataVizWidgetShell } from "@/components/analysisModules/reports/ReportLanding/comps/WidgetRenderer/comps/DataVizWidgetShell";
import { LensesTab } from "./LensesTab";
import { LensesV2Tab } from "./LensesV2";
import { TopicClusteringTreeView } from "@/components/analysisModules/TopicClusteringTreeView";


const { CaretRight, Info } = icons;

const DASHBOARD_INFO =
  "Metrics, UX and Information Architecture are actively being developed — expect changes as we improve this dashboard.";

// HACK — tenant-scoped override. These tenants are dual-agent (not split),
// so they'd normally get the classic General tab and, on prod, no Overview at
// all (the smDashboardLenses gate hides it). They want V1 Overview in place of
// General. Drop this list once Overview's visibility is driven by tenant
// config rather than inferred from annotation mode.
const TENANTS_FORCING_LENSES_OVER_GENERAL = ["ttwelve"];

const TABS = [
  { value: "new", display: "Overview" },
  { value: "lenses", display: "Overview" },
  // Overview V2 is on its way out (to be deleted); off the tab bar meanwhile.
  // { value: "lensesV2", display: "Overview V2" },
  { value: "general", display: "General" },
  { value: "findings", display: "Findings" },
  { value: "insights", display: "Insights" },
  { value: "topics", display: "Topics Old" },
  { value: "activity", display: "Activity" },
];

const GeneralTab = () => {
  const { data: metrics, isLoading, isError, error } = useGetDashboardMetrics();

  const platformConfigs = useGetQueryData("platformConfigs");
  const hasNewTopicModelling = getAnnoMode(platformConfigs) !== "legacy";

  console.log({ hasNewTopicModelling })
  if (isLoading) return <div style={{ height: "calc(100vh - 16rem)", display: "flex", alignItems: "center", justifyContent: "center" }}><LoaderCircleV2 /></div>;
  if (isError) return <UI_BODY_SM style={{ color: "#dc2626", padding: "1rem" }}>Error: {error?.response?.data?.message || error?.message}</UI_BODY_SM>;
  if (!metrics) return null;

  return (
    <div style={{ marginTop: "1.4rem" }}>
      {/* Top counts bar — shared with the Overview tab. */}
      <DashboardStatsBar metrics={metrics} />
      {/* Annotate-now widget. */}
      <div style={{ marginTop: "1.4rem" }}>
        <AIAnnotationWidget />
      </div>
      <div style={{ display: 'flex', gap: "1.4rem", marginTop: "1.4rem" }}>
        <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
          <div style={{ position: 'absolute', inset: 0, display: 'flex' }}>
            {hasNewTopicModelling ? <EvidenceFindingsView /> : <InsightsList />}
          </div>
        </div>
        <div style={{ width: '55%', flexShrink: 0, display: 'flex', flexDirection: "column", gap: '14px' }}>
          <Freshness />
          <SentimentTopicsWidget polarity="positive" title="Top Positive Topics" color="#CBEDD6" useAnnotations={hasNewTopicModelling} />
          <SentimentTopicsWidget polarity="negative" title="Top Negative Topics" color="#F5DCE6" useAnnotations={hasNewTopicModelling} />
        </div>
      </div>
      {/* <TagHealth /> */}
    </div>
  );
}

export default function SM_Dashboard() {
  const platformConfigs = useGetQueryData("platformConfigs");
  const { FEATURES_DISABLED_ON_PROD: disabledFeatures = [] } = platformConfigs || {};
  const isProd = import.meta.env.VITE_ENV === "prod";
  const lensesDisabled = isFeatureDisabledOnProd("smDashboardLenses", { disabledFeatures, isProd, tenant: getTenant() });
  const lensesV2Disabled = isFeatureDisabledOnProd("smDashboardLensesV2", { disabledFeatures, isProd, tenant: getTenant() });
  const topicsOldDisabled = isFeatureDisabledOnProd("smDashboardTopicsOld", { disabledFeatures, isProd, tenant: getTenant() });
  // Split-mode tenants (aiAnnotation.mode === "split") get the Findings tab
  // INSTEAD of the classic General tab; dual-agent and legacy tenants keep
  // General. Non-legacy mode alone does NOT discriminate — dual-agent
  // tenants pass that check too — so we also gate on mode === "split".
  const hasNewTopicModelling = getAnnoMode(platformConfigs) !== "legacy";
  const isSplitMode = getAnnoMode(platformConfigs) === "split";
  const isPmSplitMode = hasNewTopicModelling && isSplitMode;
  // Overview (V1 lenses) is hidden for split-mode tenants — Overview V2 has
  // replaced it. Its evidence layer loads every annotation on the tenant into
  // one array per lens block, with no in-flight dedupe across the six blocks,
  // which exhausts the heap on a large tenant. Non-split tenants (incl. the
  // TENANTS_FORCING_LENSES_OVER_GENERAL list) keep it until the V1 lenses code
  // is deleted outright.
  const forceLensesOverGeneral = TENANTS_FORCING_LENSES_OVER_GENERAL.includes(getTenant());
  const lensesVisible = !isPmSplitMode && (!lensesDisabled || forceLensesOverGeneral);

  const visibleTabs = TABS.filter((t) => {
    // The rebuilt dashboard. Built on the split-mode pipeline (signal
    // categories + topic tree), so it is a split-mode tab, prod included.
    if (t.value === "new") return isPmSplitMode;
    if (t.value === "lenses") return lensesVisible;
    if (t.value === "lensesV2") return !lensesV2Disabled;
    if (t.value === "general") return !isPmSplitMode && !forceLensesOverGeneral;
    if (t.value === "findings") return isPmSplitMode;
    if (t.value === "insights") return isPmSplitMode;
    // The topic-clustering tree is written only by the split-mode pipeline —
    // non-split tenants would get a permanently empty state.
    // Superseded by the Topics page (/topics); hidden on prod.
    if (t.value === "topics") return isPmSplitMode && !topicsOldDisabled;
    return true;
  });
  const [activeTab, setActiveTab] = useSearchParamsState({
    key: DASHBOARD_TAB_PARAM_KEY,
    value: lensesVisible ? "lenses" : (visibleTabs[0]?.value || "general"),
  });

  return (
    <ActivePanelProvider>
      <div>
        {/* Sticky top block: page header + dashboard tab bar pin together, so
            the tab bar lands directly below the header without a brittle
            hardcoded offset. */}
        <div style={{ position: "sticky", top: 0, zIndex: 200, backgroundColor: colors2.white }}>
          <SM_PageHeader
            title="Dashboard"
            betaBadge
            customLeftComp={
              <TooltipV2 text={DASHBOARD_INFO} placement="bottom">
                <Info size={16} color={colors2.grey40} style={{ display: "block" }} />
              </TooltipV2>
            }
          />
          {/* Tab bar wrapper — vertical padding matches the previous spacing
              (top gap + the old content marginTop as the bottom gap); side
              padding matches the content wrapper below. White, like the page
              header it pins under, so the two read as one band over the grey
              content. */}
          <div style={{ padding: `${LAYOUT_CONSTANTS.topBottomPadding}px ${LAYOUT_CONSTANTS.pagePadding}px ${LAYOUT_CONSTANTS.topBottomPadding}px`, backgroundColor: colors2.white }}>
            <TabBarV2
              options={visibleTabs}
              value={activeTab}
              onChange={(id, v) => setActiveTab(v)}
              saveValueAsString
              style={{ backgroundColor: 'transparent' }}
            />
          </div>
        </div>
        {/* Topics runs full-bleed: its SidebarLayoutV2 owns its edges (the
            filter sidebar spans the full height), so the shared side padding
            and top margin would just double-frame it. */}
        <div
          style={{
            padding: ["topics", "new"].includes(activeTab) ? 0 : `0 ${LAYOUT_CONSTANTS.pagePadding}px 0`,
            backgroundColor: colors2.grey2,
          }}
        >
          {activeTab === "new" && <NewTab />}
          {activeTab === "general" && <GeneralTab />}
          {activeTab === "lenses" && <LensesTab />}
          {activeTab === "lensesV2" && <LensesV2Tab />}
          {activeTab === "findings" && <FindingsTab />}
          {activeTab === "insights" && <InsightsTab />}
          {activeTab === "topics" && <TopicClusteringTreeView />}
          {activeTab === "activity" && (
            <div style={{ marginTop: "1.4rem" }}>
              <RecentActivity fullHeight />
            </div>
          )}
        </div>
      </div>
    </ActivePanelProvider>
  );
}
